import type { EvaluationDomain, OperandDto, WhenDto } from "../../../../../types/resolver";
import { defaultKeyOperandForEvaluationDomain } from "./defaultKeyOperand";

function isEmptyLiteralOperand(op: OperandDto): boolean {
    if (op.operandType != null) return false;
    return op.value === "";
}

/** Normalize operands for export so empty literals and invalid keys are not sent to the resolver. */
export function sanitizeOperand(op: OperandDto | undefined, domain: EvaluationDomain | undefined): OperandDto | undefined {
    if (op == null) return undefined;
    if (op.operandType === "key") {
        const k = (op.key ?? "").trim();
        if (!k) {
            return domain ? defaultKeyOperandForEvaluationDomain(domain) : undefined;
        }
        return { operandType: "key", key: k };
    }
    if (op.operandType === "derivedAttribute") {
        const n = (op.derivedAttributeName ?? "").trim();
        if (!n) return undefined;
        return { operandType: "derivedAttribute", derivedAttributeName: n };
    }
    if (op.operandType === "workItemAttribute") {
        const n = (op.workItemAttributeName ?? "").trim();
        if (!n) return undefined;
        return { operandType: "workItemAttribute", workItemAttributeName: n };
    }
    if (op.operandType === "answer") {
        return { ...op };
    }
    if (op.operandType === "aggregate") {
        const sanitizedWhen = op.when ? sanitizeWhenDto(op.when, domain) : undefined;
        return {
            operandType: "aggregate",
            operation: op.operation ?? "COUNT",
            over: op.over ?? "codes",
            ...(sanitizedWhen ? { when: sanitizedWhen } : {}),
        };
    }
    if (isEmptyLiteralOperand(op)) {
        return undefined;
    }
    return op;
}

function binaryFallbackTrue(): WhenDto {
    return { true: true };
}

function sanitizeBinary(
    left: OperandDto | undefined,
    right: OperandDto | undefined,
    domain: EvaluationDomain | undefined,
    build: (l: OperandDto, r: OperandDto) => WhenDto
): WhenDto | undefined {
    let l = sanitizeOperand(left, domain);
    let r = sanitizeOperand(right, domain);
    if (l == null && domain) {
        l = defaultKeyOperandForEvaluationDomain(domain);
    }
    if (l == null || r == null) {
        return binaryFallbackTrue();
    }
    return build(l, r);
}

/** Recursively sanitize when conditions for API/export. */
export function sanitizeWhenDto(when: WhenDto | null | undefined, domain: EvaluationDomain | undefined): WhenDto | undefined {
    if (when == null) return undefined;
    if (when.true === true) return { true: true };

    if (when.all?.conditions) {
        const conditions = when.all.conditions
            .map((c) => sanitizeWhenDto(c, domain))
            .filter((c): c is WhenDto => c != null);
        if (conditions.length === 0) return { true: true };
        return { all: { conditions } };
    }
    if (when.any?.conditions) {
        const conditions = when.any.conditions
            .map((c) => sanitizeWhenDto(c, domain))
            .filter((c): c is WhenDto => c != null);
        if (conditions.length === 0) return { true: true };
        return { any: { conditions } };
    }
    if (when.not?.condition) {
        const inner = sanitizeWhenDto(when.not.condition, domain);
        if (inner == null) return { true: true };
        return { not: { condition: inner } };
    }

    const bin = (key: "eq" | "ne" | "gt" | "gte" | "lt" | "lte") => {
        const b = when[key];
        if (!b) return undefined;
        const next = sanitizeBinary(b.left, b.right, domain, (l, r) => ({ [key]: { left: l, right: r } } as WhenDto));
        return next;
    };
    if (when.eq) return bin("eq");
    if (when.ne) return bin("ne");
    if (when.gt) return bin("gt");
    if (when.gte) return bin("gte");
    if (when.lt) return bin("lt");
    if (when.lte) return bin("lte");

    if (when.in) {
        const left = sanitizeOperand(when.in.left, domain) ?? (domain ? defaultKeyOperandForEvaluationDomain(domain) : undefined);
        if (left == null) return binaryFallbackTrue();
        const right = Array.isArray(when.in.right) ? when.in.right : [];
        return { in: { left, right } };
    }

    if (when.startsWith) {
        const sw = when.startsWith;
        const left = sanitizeOperand(sw.left, domain) ?? (domain ? defaultKeyOperandForEvaluationDomain(domain) : undefined);
        if (left == null) return binaryFallbackTrue();
        const prefix = sw.prefix ?? "";
        return { startsWith: { left, prefix } };
    }
    if (when.endsWith) {
        const ew = when.endsWith;
        const left = sanitizeOperand(ew.left, domain) ?? (domain ? defaultKeyOperandForEvaluationDomain(domain) : undefined);
        if (left == null) return binaryFallbackTrue();
        const postfix = ew.postfix ?? "";
        return { endsWith: { left, postfix } };
    }
    if (when.contains) {
        const c = when.contains;
        const left = sanitizeOperand(c.left, domain) ?? (domain ? defaultKeyOperandForEvaluationDomain(domain) : undefined);
        if (left == null) return binaryFallbackTrue();
        const substr = c.substr ?? "";
        return { contains: { left, substr } };
    }

    if (when.anyMatch) {
        const inner = sanitizeWhenDto(when.anyMatch.when, domain);
        const wn = inner ?? { true: true };
        return { anyMatch: { ...when.anyMatch, when: wn } };
    }
    if (when.noneMatch) {
        const inner = sanitizeWhenDto(when.noneMatch.when, domain);
        const wn = inner ?? { true: true };
        return { noneMatch: { ...when.noneMatch, when: wn } };
    }
    if (when.allMatch) {
        const inner = sanitizeWhenDto(when.allMatch.when, domain);
        const wn = inner ?? { true: true };
        return { allMatch: { ...when.allMatch, when: wn } };
    }

    return when;
}
